/* istanbul ignore file */
import React from 'react';
import { type CookieObj, acceptsFunctionalCookies } from '@noths/polaris-client-gdpr-compliance';
import type { SiteChromeContent } from '@noths/polaris-client-site-chrome-ui';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';

import { Seo } from 'src/components/atoms/Seo/Seo';
import { SKIP_CONTENT_TARGET_ID } from 'src/constants/elementIds';
import { buildShopSitemapGroups } from 'src/services/contentstack/buildShopSitemapGroups';
import { buildSitemapGroups } from 'src/services/contentstack/buildSitemapGroups';
import { fetchAllTaxonomyPlpsForSitemap } from 'src/services/contentstack/fetchAllTaxonomyPlpsForSitemap';
import { fetchDepartmentPages } from 'src/services/contentstack/fetchDepartmentPages';
import { fetchGlobalContent } from 'src/services/contentstack/fetchGlobalContent';
import { fetchAllProductListingPagesForSitemap } from 'src/services/contentstack/fetchProductListingPagesForSitemap';
import { formatShopLevel1SegmentLabel } from 'src/services/contentstack/shopSitemapUrl';
import * as styles from 'src/styles/sitemap.styles';
import type { PageMetadata } from 'src/types/PageProps';
import { getPageMetadata } from 'src/utils/getPageMetadata';

interface SitemapPageProps {
  departmentGroups: ReturnType<typeof buildSitemapGroups>['departmentGroups'];
  otherPages: ReturnType<typeof buildSitemapGroups>['otherPages'];
  pageEventCategory: string;
  pageMetadata: PageMetadata;
  shopGroups: ReturnType<typeof buildShopSitemapGroups>;
  siteChromeContent: SiteChromeContent | null;
}

export const getServerSideProps: GetServerSideProps<SitemapPageProps> = async (context) => {
  const pageMetadata: PageMetadata = {
    ...getPageMetadata(context),
    functionalCookiesConsented: acceptsFunctionalCookies(context.req.cookies as CookieObj),
  };

  const [siteChromeContent, departments, plpItems, taxonomyPlpItems] = await Promise.all([
    fetchGlobalContent({
      featureFlags: pageMetadata.featureFlags,
      isPreview: pageMetadata.isPreview,
    }).then(([chrome]) => chrome),
    fetchDepartmentPages(pageMetadata.isPreview),
    fetchAllProductListingPagesForSitemap(pageMetadata.isPreview),
    fetchAllTaxonomyPlpsForSitemap(pageMetadata.isPreview),
  ]);

  const { departmentGroups, otherPages } = buildSitemapGroups(departments, plpItems);
  const shopGroups = buildShopSitemapGroups(taxonomyPlpItems);

  return {
    props: {
      departmentGroups,
      otherPages,
      pageMetadata,
      pageEventCategory: 'Sitemap',
      shopGroups,
      siteChromeContent,
    },
  };
};

const SitemapPage = ({
  departmentGroups,
  otherPages,
  shopGroups,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => (
  <div id={SKIP_CONTENT_TARGET_ID}>
    <Seo
      description="Browse department pages and product listing pages on notonthehighstreet.com."
      title="Sitemap | notonthehighstreet.com"
    />
    <main css={styles.main}>
      <h1 css={styles.pageTitle}>Sitemap</h1>

      {departmentGroups.map(({ department, productListingPages }) => (
        <section
          aria-labelledby={`dept-${department.url}`}
          css={styles.section}
          key={department.url}
        >
          <h2 css={styles.sectionTitle} id={`dept-${department.url}`}>
            <a href={department.url}>{department.title}</a>
          </h2>
          {productListingPages.length > 0 ? (
            <ul css={styles.linkGrid}>
              {productListingPages.map((plp) => (
                <li css={styles.linkGridItem} key={plp.url}>
                  <a href={plp.url}>{plp.heading}</a>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ))}

      {otherPages.length > 0 ? (
        <section aria-labelledby="sitemap-other" css={styles.section}>
          <h2 css={styles.sectionTitle} id="sitemap-other">
            Other pages
          </h2>
          <ul css={styles.linkGrid}>
            {otherPages.map((page) => (
              <li css={styles.linkGridItem} key={page.url}>
                <a href={page.url}>{page.heading}</a>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {shopGroups.length > 0 ? (
        <section aria-labelledby="sitemap-shop" css={styles.section}>
          <h2 css={styles.sectionTitle} id="sitemap-shop">
            Shop pages
          </h2>
          {shopGroups.map(({ level1Segment, links, sectionTitle }) => {
            const subsectionId = `shop-${level1Segment}`;
            const hasLinkedTitle = sectionTitle !== null;

            return (
              <section aria-labelledby={subsectionId} css={styles.section} key={level1Segment}>
                {hasLinkedTitle ? (
                  <h2 css={styles.sectionTitle} id={subsectionId}>
                    <a href={sectionTitle.url}>{sectionTitle.heading}</a>
                  </h2>
                ) : (
                  <h2 css={styles.shopGroupFallbackTitle} id={subsectionId}>
                    {formatShopLevel1SegmentLabel(level1Segment)}
                  </h2>
                )}
                {links.length > 0 ? (
                  <ul css={styles.linkGrid}>
                    {links.map((link) => (
                      <li css={styles.linkGridItem} key={link.url}>
                        <a href={link.url}>{link.heading}</a>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </section>
            );
          })}
        </section>
      ) : null}
    </main>
  </div>
);

export default SitemapPage;
